/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import type { ScanGroupOptimized } from '@kaiizen/kai-scan-template-system';
import { useMemo } from 'react';

import { groupHasRoomAreaTemplate } from '@kaiizen/kai-scan-template-system';

import { styleguideColors as COLORS } from '@/Client/Styles/kaiShikiTokens';
import { useFeatureFlags } from '@kaiizen/kai-pro-api-react-state';
import { styleguideFontFamily as FONT } from '@kaiizen/kai-shiki/core';

import { getFlooringAreaValueNames } from '@/Client/Lib/roomGroupMeasurementsLib';

import { getFlooringHatchPatterns } from './FlooringHatchSwatch';
import { FloorPlanGroupTiles } from './FloorPlanGroupTiles';
import { FloorPlanOverviewHeader } from './FloorPlanOverviewHeader';
import { FocusedFloorPlanAssembler } from './FocusedFloorPlanAssembler';
import { FocusedFloorPlanEditor } from './FocusedFloorPlanEditor';
import { FocusPanelTitlebar } from './FocusPanelTitlebar';
import { FLOOR_PLAN_PAGE, type ScanFocusPanelScopeProps } from './scanFocusPanelTypes';
import { useRoomOpening } from './useRoomOpening';

/**
 * The two scope-level views: the room index (the `measurements` page) or the whole-property
 * assembler (the `floor-plan` page). Which one shows is the route's business, so the browser's
 * history and a shared link keep it.
 */
export type MeasurementsView = 'rooms' | 'floorPlan';

const MEASUREMENTS_VIEWS: { id: MeasurementsView; label: string }[] = [
  { id: 'rooms', label: 'Rooms' },
  { id: 'floorPlan', label: 'Floor plan' },
];

/** The view switch wears the panel's own tab language: mono uppercase, ink where selected. */
const VIEW_SWITCH_STYLE = css`
  label: MEASUREMENTS_VIEW_SWITCH_STYLE;
  display: inline-flex;
  align-items: stretch;
  border: 1px solid ${COLORS.stroke};
  background: ${COLORS.white};
`;

const VIEW_OPTION_STYLE = css`
  label: MEASUREMENTS_VIEW_OPTION_STYLE;
  padding: 7px 12px;
  border: 0;
  border-left: 1px solid ${COLORS.stroke};
  background: transparent;
  color: ${COLORS.midtone};
  font-family: ${FONT.mono};
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  cursor: pointer;

  &:first-of-type {
    border-left: 0;
  }

  &:hover {
    color: ${COLORS.ink};
  }
`;

const VIEW_OPTION_SELECTED_STYLE = css`
  label: MEASUREMENTS_VIEW_OPTION_SELECTED_STYLE;
  background: ${COLORS.ink};
  color: ${COLORS.white};
  cursor: default;

  &:hover {
    color: ${COLORS.white};
  }
`;

/** Sits above the room index, flush-left, with the assembler variant filling the body below. */
const VIEW_SWITCH_ROW_STYLE = css`
  label: MEASUREMENTS_VIEW_SWITCH_ROW_STYLE;
  display: flex;
  margin-bottom: 18px;
`;

const VIEW_SWITCH_ROW_FLUSH_STYLE = css`
  label: MEASUREMENTS_VIEW_SWITCH_ROW_FLUSH_STYLE;
  flex-shrink: 0;
  padding: 0 24px 12px;
  margin-bottom: 0;
`;

const MeasurementsViewSwitch = ({ view, onView, flush }: { view: MeasurementsView; onView: (view: MeasurementsView) => void; flush: boolean }) => (
  <div css={[VIEW_SWITCH_ROW_STYLE, flush && VIEW_SWITCH_ROW_FLUSH_STYLE]}>
    <span css={VIEW_SWITCH_STYLE} role='group' aria-label='Measurements view' data-testid='measurements-view-switch'>
      {MEASUREMENTS_VIEWS.map(option => {
        const selected = option.id === view;
        return (
          <button
            key={option.id}
            type='button'
            css={[VIEW_OPTION_STYLE, selected && VIEW_OPTION_SELECTED_STYLE]}
            aria-pressed={selected}
            data-view={option.id}
            onClick={selected ? undefined : () => onView(option.id)}
          >
            {option.label}
          </button>
        );
      })}
    </span>
  </div>
);

/**
 * The Floor plan focus panel. At overall-scope level it shows the measurement
 * overview and the room index (one card per room group) — and, behind the
 * `fullFloorPlans` flag, a second view: the floor plan assembler, where the
 * rooms are positioned relative to each other level by level. The view is the
 * page: the room index at `measurements`, the assembler at `floor-plan`, and
 * the switch navigates between them. With a group or task selected, either
 * page opens the full floor plan editor for that group's room — a task's floor
 * plan is a property of the room it belongs to.
 */
export const FocusedMeasurementsPanel = ({
  groups,
  listedSqft,
  onOpenProjectMeasurements,
  ...scopeProps
}: ScanFocusPanelScopeProps & {
  groups: readonly ScanGroupOptimized[];
  /** The property's listed square footage, shown against the measured total. */
  listedSqft?: number | null;
  onOpenProjectMeasurements?: () => void;
}) => {
  const { featureFlags } = useFeatureFlags();
  const view: MeasurementsView = scopeProps.activePanel === FLOOR_PLAN_PAGE ? 'floorPlan' : 'rooms';
  const selectView = (next: MeasurementsView) => scopeProps.onSelectPanel(next === 'floorPlan' ? FLOOR_PLAN_PAGE : 'measurements');
  const assemblerAvailable = featureFlags.fullFloorPlans;
  const showAssembler = assemblerAvailable && view === 'floorPlan';
  const atScopeLevel = scopeProps.scopeLevel === 'scope';
  const { holdOpening, openingFor } = useRoomOpening(scopeProps.selectedGroupId);
  const selectedGroup = scopeProps.selectedGroupId
    ? groups.find(group => group.id === scopeProps.selectedGroupId)
    : undefined;
  // Assigned once for the whole panel so a material keeps one hatch across the
  // overview chips and every card, whatever areas happen to be filled in.
  const { flooringHatchPatterns, flooringValueNames } = useMemo(() => {
    const valueNames = getFlooringAreaValueNames(groups.filter(groupHasRoomAreaTemplate));
    return {
      flooringHatchPatterns: getFlooringHatchPatterns(valueNames),
      flooringValueNames: valueNames,
    };
  }, [groups]);
  const viewSwitch = assemblerAvailable && atScopeLevel
    ? <MeasurementsViewSwitch view={view} onView={selectView} flush={showAssembler} />
    : null;
  return (
    <FocusPanelTitlebar {...scopeProps} bodyVariant={atScopeLevel && !showAssembler ? 'default' : 'flush'}>
      {atScopeLevel && showAssembler
        ? (
          <>
            {viewSwitch}
            <FocusedFloorPlanAssembler
              onOpenRoom={(groupId, view) => {
                holdOpening(groupId, view);
                scopeProps.onSelectGroup?.(groupId);
              }}
            />
          </>
        )
        : atScopeLevel
        ? (
          <>
            {viewSwitch}
            <FloorPlanOverviewHeader
              flooringHatchPatterns={flooringHatchPatterns}
              flooringValueNames={flooringValueNames}
              groups={groups}
              listedSqft={listedSqft}
              onViewTable={onOpenProjectMeasurements}
            />
            <FloorPlanGroupTiles
              flooringHatchPatterns={flooringHatchPatterns}
              flooringValueNames={flooringValueNames}
              groups={groups}
              onSelectGroup={scopeProps.onSelectGroup}
            />
          </>
        )
        : selectedGroup
        ? (
          // Keyed by group so the unsaved-changes tracking resets per room.
          <FocusedFloorPlanEditor key={selectedGroup.id} group={selectedGroup} defaultView={openingFor(selectedGroup.id)} />
        )
        : null}
    </FocusPanelTitlebar>
  );
};
